import { useGetMerchantAppProductsQuery } from "Api/Queries/Merchants/useGetMerchantAppProductsQuery";
import { usePayPointsQuery } from "Api/Queries/PayPoints/usePayPointsQuery";
import { PaypointsNfcAssignList } from "Components/Nfc/NfcAssignmentPaypointsList";
import { CenteredCircularProgress } from "Components/Shared/CenteredCircularProgress";
import Error404Page from "Pages/Error/Error404Page";
import { Resources, useResource } from "Translations/Resources";
import { Box, Stack, Typography } from "@mui/material";
import { useParams } from "react-router-dom";
import { useCurrentUserRole } from "Hooks/useCurrentUserAccessRole";
import { AccessRoleCodes } from "Models/AccessRoleCodes";
import { useMerchantsListQuery } from "Api/Queries/Merchants/useGetMerchantsQuery";
import { useSearchParams } from "Navigation/useSearchParams";
import { MerchantSelectionTable } from "Components/Nfc/MerchantSelectionTable";
import { AuthenticatedLayout } from "Components/Layout/AuthenticatedLayout";
import { StyledPageWrapper } from "Components/Shared/StyledComponents";
import { PrimaryPaper } from "Components/Shared/PrimaryPaper";

const NfcAssignPage = () => {
  const { id } = useParams<{ id: string }>();
  const { t } = useResource();
  const currentUserRole = useCurrentUserRole();
  // No pagination — NFC assign needs all merchants for selection UI.
  const { data, isLoading, isRefetching } = useMerchantsListQuery({
    limit: 100,
  });
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedMerchantId = searchParams.get("merchantId");

  if (!id) {
    return <Error404Page />;
  }

  if (currentUserRole === AccessRoleCodes.Merchant) {
    return (
      <Layout>
        <MerchantNfcPaypointAssigner nfcCardNo={id} />
      </Layout>
    );
  }

  if (
    currentUserRole === AccessRoleCodes.Hunter ||
    currentUserRole === AccessRoleCodes.Admin
  ) {
    if (selectedMerchantId) {
      return (
        <Layout>
          <MerchantNfcPaypointAssigner
            nfcCardNo={id}
            merchantPublicID={selectedMerchantId}
          />
        </Layout>
      );
    }

    return (
      <Layout>
        <Stack gap={3}>
          <Typography variant="h5">
            {t(Resources.Nfc.SelectMerchant)}
          </Typography>
          <Box sx={{ display: "grid" }}>
            <MerchantSelectionTable
              data={data?.items ?? []}
              isLoading={isLoading}
              isRefetching={isRefetching}
              onRowClick={(merchant) => {
                setSearchParams({ merchantId: merchant.publicID });
              }}
            />
          </Box>
        </Stack>
      </Layout>
    );
  }
  return null;
};

export default NfcAssignPage;

type MerchantNfcPaypointAssignerProps = {
  nfcCardNo: string;
  merchantPublicID?: string;
};

const MerchantNfcPaypointAssigner = ({
  nfcCardNo,
  merchantPublicID,
}: MerchantNfcPaypointAssignerProps) => {
  const { data: merchantProducts, isLoading: isMerchantProductsLoading } =
    useGetMerchantAppProductsQuery(merchantPublicID);
  const { data: paypointsData, isLoading: isLoadingPaypoints } =
    usePayPointsQuery(0, 1000, merchantPublicID);
  const { t } = useResource();

  if (isLoadingPaypoints || isMerchantProductsLoading) {
    return <CenteredCircularProgress color="grey" />;
  }

  if (!merchantProducts) {
    return <div>{t(Resources.Merchant.NoProductAssigned)}</div>;
  }

  const productsWithPaypoints = merchantProducts.filter(
    ({ productDto }) => productDto.nfcPayPointsLimit > 0,
  );

  if (!productsWithPaypoints.length) {
    return <div>{t(Resources.Merchant.NoPaypointProduct)}</div>;
  }

  const paypoints = paypointsData?.items ?? [];

  return (
    <Stack gap={3}>
      <Typography variant="h5">{t(Resources.Nfc.SelectPaypoint)}</Typography>
      <PrimaryPaper>
        {productsWithPaypoints.map(
          ({ productDto: { code, name, nfcPayPointsLimit, productID } }) => (
            <PaypointsNfcAssignList
              key={code}
              title={name}
              limit={nfcPayPointsLimit}
              paypoints={paypoints.filter(
                (paypoint) => paypoint.productID === productID,
              )}
              productID={productID}
              nfcCardNo={nfcCardNo}
              merchantPublicID={merchantPublicID}
            />
          ),
        )}
      </PrimaryPaper>
    </Stack>
  );
};

const Layout = ({ children }: { children: React.ReactNode }) => {
  const { t } = useResource();

  return (
    <AuthenticatedLayout
      title={t(Resources.Nfc.AssignToPaypoint)}
      backNavigation={{
        mode: "historyStack",
        fallbackUrl: null,
      }}
    >
      <StyledPageWrapper>
        <div className="left-content" style={{ width: "100%", height: "100%" }}>
          {children}
        </div>
      </StyledPageWrapper>
    </AuthenticatedLayout>
  );
};
